import type { Initiative, Status } from "@/lib/types";
import { Card } from "./ui/card";
import { PriorityBadge } from "./ui/badge";
import { fmtDate } from "@/lib/quarter";

const COLUMNS: { status: Status; label: string }[] = [
  { status: "planned", label: "Planned" },
  { status: "in_progress", label: "In progress" },
  { status: "done", label: "Done" },
  { status: "cancelled", label: "Cancelled" },
];

interface Props {
  initiatives: Initiative[];
  onSelect: (i: Initiative) => void;
}

export function KanbanView({ initiatives, onSelect }: Props) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
      {COLUMNS.map(({ status, label }) => {
        const items = initiatives.filter((i) => i.status === status);
        return (
          <div key={status} className="flex flex-col gap-3 min-h-[300px]">
            <div className="flex items-center justify-between px-1">
              <h3 className="text-sm font-semibold uppercase tracking-wide">{label}</h3>
              <span className="text-xs text-muted-foreground rounded-full bg-secondary px-2 py-0.5">
                {items.length}
              </span>
            </div>
            <div className="flex flex-col gap-2">
              {items.map((it) => (
                <Card
                  key={it.id}
                  className="p-3 cursor-pointer hover:border-primary/40 hover:shadow-md transition-all"
                  onClick={() => onSelect(it)}
                >
                  <div className="flex items-start justify-between gap-2 mb-1.5">
                    <PriorityBadge priority={it.priority} />
                    <span
                      className="text-[10px] font-medium px-1.5 py-0.5 rounded border"
                      style={{
                        borderColor: `${it.team_color}66`,
                        color: it.team_color,
                        backgroundColor: `${it.team_color}14`,
                      }}
                    >
                      {it.team_name}
                    </span>
                  </div>
                  <div className="text-sm font-medium leading-snug mb-1">{it.title}</div>
                  <div className="text-xs text-muted-foreground flex items-center justify-between">
                    <span>{it.product_name}</span>
                    <span>
                      {fmtDate(it.start_date)} → {fmtDate(it.end_date)}
                    </span>
                  </div>
                  {it.owner && (
                    <div className="text-[10px] text-muted-foreground mt-1.5">
                      {it.owner}
                    </div>
                  )}
                </Card>
              ))}
              {items.length === 0 && (
                <div className="text-xs text-muted-foreground italic px-1 py-4 text-center border border-dashed rounded-md">
                  Empty
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
